import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'hooks/useCustomQuery';
import getCompanyPlatformNumbers from 'API/Company/getCompanyPlatformNumbers';
import getAllTrunks from '@/API/Trunk/getAllTrunks';
import useGetOperatorConnectUsages from 'pages/telephony/numbers/queries/useGetOperatorConnectUsages';
import { useGetCountriesList } from 'pages/telephony/numbers/queries/useGetCountriesList';
import { PlatformEnums } from '@/utils/platforms';
import ScopeType from 'utils/enums/ScopeType';
import { TEAMS_BULK_NUMBERS_KEY } from '../constants';
import { getTenantTrunkKinds, toBulkRows } from '../utils/rows';

const EMPTY = [];

/**
 * Loads everything the Teams bulk operations page needs: the company's Teams
 * platform numbers, its Teams trunks (which carry the calling profiles), the
 * Operator Connect usage types and the country list.
 *
 * Trunks and calling profiles are company-level, so the page only works at
 * Company scope for a company that has the Microsoft Teams platform.
 */
const useTeamsBulkNumbers = () => {
    const { currentCompany, currentCompanyPlatforms } = useSelector(
        (state) => state.navigationLists,
    );
    const { currentScope } = useSelector((state) => state.login);

    const isCompanyScope = currentScope === ScopeType.COMPANY;
    const hasTeams = !!currentCompanyPlatforms?.some(
        (platform) => platform.id === PlatformEnums.TEAMS,
    );
    const enabled = isCompanyScope && !!currentCompany && hasTeams;

    const numbersQuery = useQuery({
        queryKey: [TEAMS_BULK_NUMBERS_KEY, currentCompany],
        queryFn: () =>
            getCompanyPlatformNumbers(
                currentCompany,
                PlatformEnums.TEAMS,
                currentScope,
            ),
        enabled,
        refetchOnWindowFocus: false,
    });

    const trunksQuery = useQuery({
        queryKey: ['teamsBulkTrunks', currentCompany],
        queryFn: () =>
            getAllTrunks(currentCompany, currentScope, PlatformEnums.TEAMS),
        enabled,
        refetchOnWindowFocus: false,
    });

    const trunks = trunksQuery.data ?? EMPTY;
    const tenant = useMemo(() => getTenantTrunkKinds(trunks), [trunks]);

    const usagesQuery = useGetOperatorConnectUsages(
        currentCompany,
        currentScope,
        enabled && tenant.oc,
    );
    const countriesQuery = useGetCountriesList({ currentCompany });

    const usages = usagesQuery.data ?? EMPTY;
    const countries = countriesQuery.data ?? EMPTY;
    const platformNumbers = numbersQuery.data?.platformNumbers ?? EMPTY;

    const rows = useMemo(
        () => toBulkRows(platformNumbers, { trunks, usages, countries }),
        [platformNumbers, trunks, usages, countries],
    );

    return {
        rows,
        trunks,
        usages,
        countries,
        tenant,
        isCompanyScope,
        hasTeams,
        // null until the navbar has loaded the company's platforms
        hasLoadedPlatforms: currentCompanyPlatforms != null,
        currentCompany,
        currentScope,
        refetch: () =>
            Promise.all([numbersQuery.refetch(), trunksQuery.refetch()]),
        isLoading:
            numbersQuery.isInitialLoading || trunksQuery.isInitialLoading,
        isError: numbersQuery.isError || trunksQuery.isError,
    };
};

export default useTeamsBulkNumbers;
